// D3-6..D3-10 — THE USAGE PAGE, THE TRANSCRIPTS, THE EXPORT, AND THE REVIEW QUEUE. THE LAW.
//
// Imports nothing, so the server enforces with it, the dashboard renders from it, and
// `scripts/d3-usage.mjs` RUNS it every build.
//
// ---------------------------------------------------------------------------------------------
// READ THIS FIRST: THERE ARE TWO SURFACES HERE AND THEY HAVE DIFFERENT RULES ON PURPOSE.
//
// D3-2 says Ask Lala conversation content is NEVER shown *in the support view*. D3-7, revised by
// the owner in the same message to PATH B, opens FULL transcripts on the *usage page*, behind owner
// sign-in plus the support passcode, ledgered per open. Those are not in conflict and neither one
// may be softened into the other: the support view is the one that stays shut, and the usage page
// is a separate door with a separate allowlist and a heavier log.
//
// So `src/supportView.js` keeps `lala-conversation` in its FORBIDDEN list, and this file carries the
// usage page's own list. Both fail closed. Both name their refusals out loud.
// ---------------------------------------------------------------------------------------------

// What the USAGE PAGE may open. Anything not on this list is refused, including a surface invented
// next year. `bulk-export` is named as a REFUSAL rather than simply left off, because "NO BULK
// EXPORT" is her explicit word and a named refusal is testable.
export const USAGE_ALLOWED = [
  "generation-history", // what they generated, when, in which room, at what cost class
  "lala-usage",         // conversations, turns against their cap, tools, gate trips
  "transcript",         // PATH B: the full conversation, both sides, read-only
  "transcript-export",  // ONE conversation as a timestamped PDF, ledgered
  "review-queue",       // D3-9: the machine-flagged conversations, owner-facing
];

export const USAGE_FORBIDDEN = [
  "journal",        // every D3 wall still stands here
  "notepaper",
  "bookmarks",
  "saved-verses",
  "shelf-notes",
  "birth-full",
  "bulk-export",    // HER WORD. One conversation at a time or nothing.
];

export const usageMay = (surface) => USAGE_ALLOWED.includes(String(surface || ""));
export const usageRefuses = (surface) => USAGE_FORBIDDEN.includes(String(surface || ""));

// ---------------------------------------------------------------------------------------------
// D3-7 — COST CLASS.
//
// "COST CLASS", not a dollar figure. A per-reading price would be a number invented on this screen
// and stale the day a vendor changes a plan; what she actually needs to see is WHICH KIND OF SPEND a
// row represents. Three classes, and they are read off which engine the family runs on, per the two
// vendors CLAUDE.md documents plus the one model:
//
//   chart+model  a cast on an astrology vendor AND an interpretation pass. The expensive kind.
//   model        an interpretation pass only. No vendor cast.
//   free         built in code, no paid call at all (the seal has been drawn in code since S4).
//
// UNLISTED FAMILIES COME BACK "unknown" AND SAY SO ON SCREEN. They are never quietly folded into a
// cheap class — `scripts/d3-usage.mjs` asserts every generation kind PRESENT IN THE LIVE EVENTS
// TABLE has a class, so an unclassified new generator shows up as a red gate rather than as a
// confident wrong number.
// ---------------------------------------------------------------------------------------------
export const COST_CLASSES = {
  "chart+model": "A chart cast on an astrology vendor, plus an interpretation pass.",
  model: "An interpretation pass. No vendor cast.",
  free: "Built in code. No paid call.",
  unknown: "Not yet classified. It is shown as unknown rather than guessed at.",
};

// Keyed on the generation kind's FAMILY (the part before the colon), because kinds are namespaced
// ("mini-report:ideal-job", "astrocartography:map") and a family shares an engine.
export const COST_BY_FAMILY = {
  // AstrologyAPI (astrologyapi.com) — charts, transits, numerology raw data — then Anthropic.
  "birth-chart": "chart+model",
  "person-birth-chart": "chart+model",
  "astrology-report": "chart+model",
  "astrology-ask": "chart+model",
  "astrology-tradition": "chart+model",
  numerology: "chart+model",
  compatibility: "chart+model",
  synastry: "chart+model",
  personality: "chart+model",
  "mini-report": "chart+model",
  bubble: "chart+model",
  prediction: "chart+model",
  // AstroAPI (astrology-api.io, Business plan) — then Anthropic.
  "human-design": "chart+model",
  "hd-compatibility": "chart+model",
  "find-your-type": "chart+model",
  astrocartography: "chart+model",
  // Anthropic only. No vendor cast in the path.
  tarot: "model",
  "tarot-spread": "model",
  dream: "model",
  spell: "model",
  craft: "model",
  petition: "model",
  "keys-working": "model",
  "moses-working": "model",
  "oils-working": "model",
  "herb-working": "model",
  "cauldron-working": "model",
  "palm-reading": "model",
  // S4/SG-1: the seal is drawn in code and rasterised. The ONE paid call left on that path is the
  // name resolver, which is a text call, so this is a model row and not a free one.
  seal: "model",

  // ---------------------------------------------------------------------------------------------
  // THE SAVED REPORT **TYPES**, WHICH ARE A SECOND NAMING SYSTEM AND NOT THE SAME AS THE KINDS.
  //
  // A generation KIND is namespaced with a colon ("astrocartography:map", "craft:chant"). The saved
  // report TYPE is hyphenated and sometimes drops the family entirely ("astrocartography-map",
  // "chant"). They mostly coincide, which is exactly why this bit them: the usage page classifies
  // what is ON THE SHELF, so it reads TYPES, and eight real types were coming back "unknown" on
  // screen while the kind list looked complete. Measured off the live reports table, not guessed.
  // Both systems are listed here so one table answers for both, and the fixture holds this against
  // the live types as well as the live kinds.
  // ---------------------------------------------------------------------------------------------
  "astrocartography-map": "chart+model",
  "astrocartography-location": "chart+model",
  "astrocartography-relocation": "chart+model",
  "bubble-reading": "chart+model",
  "question-reading": "chart+model",
  relationship: "chart+model",
  "human-design-compat": "chart+model",
  synastry: "chart+model",
  chant: "model",
  affirmation: "model",
  incantation: "model",
  // A note is the member's own text saved to a shelf. Nothing is generated for it at all.
  note: "free",
};

export function costClassFor(kind) {
  const family = String(kind || "").split(":")[0].trim();
  if (!family) return "unknown";
  return COST_BY_FAMILY[String(kind || "").trim()] || COST_BY_FAMILY[family] || "unknown";
}

export const costClassNote = (cls) => COST_CLASSES[cls] || COST_CLASSES.unknown;

// ---------------------------------------------------------------------------------------------
// D3-9 — THE MONITORING QUEUE. "Her duty made workable: surface the conversations the machine flags
// so she monitors BY EXCEPTION instead of reading everything."
//
// EVERY GATE NAME BELOW WAS MEASURED OFF THE LIVE `gate_log`, NOT GUESSED. That matters more here
// than anywhere else in this file: a flag family wired to a gate name that nothing writes is an
// empty queue that looks like a clean week. `scripts/d3-usage.mjs` holds this table against the
// gate names the SERVER ACTUALLY LOGS, so a renamed gate turns the queue red instead of silent.
//
// The five families are hers, verbatim from the order. What sits under each is the measured mapping.
// ---------------------------------------------------------------------------------------------
export const REVIEW_FLAGS = [
  {
    key: "gate-trip",
    label: "Gate trips",
    why: "A gate caught the reply and made her re-render it. The member never saw the rejected one.",
    gates: ["voice", "generated-voice", "lala-register", "lala-name-register", "spoken-word", "seal-accuracy", "reading-first"],
  },
  {
    key: "tier-refusal",
    label: "Tier refusals",
    why: "A member asked for something their tier does not open, or hit the daily turn cap.",
    gates: ["lala-tier", "lala-tier-hardstop", "lala-cap"],
  },
  {
    key: "frustration",
    label: "Member frustration",
    why: "Repeated corrections or a dispute turn: the member is pushing back on what she said.",
    gates: ["accuracy-dispute", "lala-correction-collapse"],
  },
  {
    key: "fact-check",
    label: "Fact-checks fired",
    why: "A claim was checked against the member's real chart or record and did not hold.",
    gates: ["lala-house-model", "data-integrity", "biography"],
  },
  {
    key: "security",
    label: "Security",
    why: "A probe, an injection pattern, or a confidentiality gate trip.",
    gates: ["probe", "confidentiality"],
  },
  {
    key: "error",
    label: "Errors mid-conversation",
    why: "A turn failed and was refunded rather than charged. The closest true record of a broken turn.",
    gates: ["lala-turn-refund"],
  },
];

// Which family a gate_log row belongs to, or "" if it is not a review-queue concern. Deterministic
// and total: one gate can only ever land in one family, which the fixture asserts.
export function reviewFlagFor(gate) {
  const g = String(gate || "");
  for (const f of REVIEW_FLAGS) if (f.gates.includes(g)) return f.key;
  return "";
}

export const REVIEW_FLAG_KEYS = REVIEW_FLAGS.map((f) => f.key);
export const REVIEW_GATES = REVIEW_FLAGS.flatMap((f) => f.gates);
export const reviewFlagLabel = (key) => (REVIEW_FLAGS.find((f) => f.key === key) || {}).label || String(key || "");
export const reviewFlagWhy = (key) => (REVIEW_FLAGS.find((f) => f.key === key) || {}).why || "";

// ---------------------------------------------------------------------------------------------
// THE COPY. Owner-facing and plain, because the member-facing disclosure already exists in the FAQ
// and Terms and this is the other half of the same truth.
// ---------------------------------------------------------------------------------------------
export const TRANSCRIPT_NOTE = "The full conversation, both sides, read-only. Opening it writes a line to the action log: which member, which conversation, when, by which account.";
export const REASON_NOTE = "Why you opened it (optional, kept with the log line).";
export const EXPORT_NOTE = "Exports this one conversation as a timestamped PDF, and logs the export. There is no bulk export.";
export const NO_BULK_NOTE = "One conversation at a time. There is no way to export them all.";
// R88 — what the export says when it did not happen. The house line only: where the SERVER sent its
// own words (a locked door, a 404, a 500) those are what she reads, because a refusal she can act on
// is worth more than a tidy sentence (R72).
export const EXPORT_FAILED = "That conversation could not be exported. Nothing was taken out of the building.";
export const EXPORT_BUSY = "Preparing the PDF...";
export const REVIEW_NOTE = "Only the conversations the gates flagged. This is monitoring by exception, not reading everything.";

// The export's filename. ONE law, so the server names the file and the client only obeys — the same
// rule T1-DL set for report downloads.
export function transcriptFileName({ email = "", conversationId = "", at = "" } = {}) {
  const who = String(email || "member").split("@")[0].replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "member";
  const day = String(at || "").slice(0, 10) || "undated";
  const id = String(conversationId || "drawer").replace(/[^a-z0-9-]+/gi, "").slice(0, 24) || "drawer";
  return `12thhouse-transcript-${who}-${day}-${id}.pdf`;
}
